<template>
  <div
    v-if="visible"
    class="drawer-overlay"
    @click.self="close"
  >
    <transition name="slide-left" appear>
      <div class="drawer-content">

        <!-- HEADER -->
        <div class="drawer-header">
          <div>
            <h2 class="drawer-title">Добавить опыт работы</h2>
            <p class="drawer-subtitle">
              Укажите компанию, должность и период работы
            </p>
          </div>

          <button
            class="close-button"
            type="button"
            :disabled="isSaving"
            aria-label="Закрыть"
            @click="close"
          >
            &#10005;
          </button>
        </div>

        <!-- BODY -->
        <div class="drawer-body">
          <div
            class="form-content"
            :class="{ 'form-content--disabled': isSaving }"
          >
            <div class="field-group">
              <label class="field-label">
                Компания
              </label>

              <CompanyAutocomplete
                v-model="form.company"
              />
            </div>

            <transition name="fields-fade">
              <div
                v-if="form.company"
                class="career-fields"
              >
                <div class="field-group">
                  <label
                    class="field-label"
                    for="career-specialty"
                  >
                    Должность
                  </label>

                  <input
                    id="career-specialty"
                    v-model="form.specialty"
                    type="text"
                    placeholder="Например, iOS-разработчик"
                    class="text-input input-full"
                    :disabled="isSaving"
                  />
                </div>

                <div class="date-fields">
                  <div class="date-group">
                    <label
                      class="field-label"
                      for="career-start-date"
                    >
                      Начало работы
                    </label>

                    <input
                      id="career-start-date"
                      v-model="form.start_date"
                      type="text"
                      inputmode="numeric"
                      placeholder="2017 г."
                      class="text-input"
                      :disabled="isSaving"
                      @input="formatYear('start_date')"
                    />
                  </div>

                  <div class="date-group">
                    <label
                      class="field-label"
                      for="career-end-date"
                    >
                      Окончание работы
                    </label>

                    <input
                      id="career-end-date"
                      v-model="form.end_date"
                      type="text"
                      inputmode="numeric"
                      placeholder="2022 г."
                      class="text-input"
                      :disabled="isSaving"
                      @input="formatYear('end_date')"
                    />
                  </div>
                </div>
              </div>
            </transition>
          </div>

          <!-- Локальный loader поверх формы -->
          <transition name="fade">
            <div
              v-if="isSaving"
              class="saving-overlay"
            >
              <div class="saving-card">
                <div class="saving-spinner"></div>

                <div class="saving-text">
                  <strong>Сохраняем опыт работы</strong>
                  <span>Это займёт несколько секунд</span>
                </div>
              </div>
            </div>
          </transition>
        </div>

        <!-- FOOTER -->
        <div class="drawer-footer">
          <button
            class="cancel-button"
            type="button"
            :disabled="isSaving"
            @click="close"
          >
            Отмена
          </button>

          <button
            class="save-btn"
            type="button"
            :disabled="!isFormValid || isSaving"
            @click="handleSave"
          >
            <template v-if="isSaving">
              <span class="button-spinner"></span>
              <span>Сохраняем...</span>
            </template>

            <template v-else>
              Сохранить
            </template>
          </button>
        </div>

      </div>
    </transition>
  </div>
</template>

<script setup>
import { computed, ref } from "vue";
import CompanyAutocomplete from "./CompanyAutocomplete.vue";
import SummaryService from "../../api/SummaryService.js";

const props = defineProps({
  visible: {
    type: Boolean,
    default: false
  },

  userId: {
    type: String,
    default: ""
  }
});

const emit = defineEmits([
  "close",
  "save",
  "snackbar"
]);

const isSaving = ref(false);

const form = ref({
  company: null,
  specialty: "",
  start_date: "",
  end_date: ""
});

const isFormValid = computed(() => {
  const companyName = getCompanyName(form.value.company);
  const specialty = form.value.specialty.trim();
  const startYear = extractYear(form.value.start_date);
  const endYear = extractYear(form.value.end_date);

  return Boolean(
    companyName &&
    specialty &&
    startYear.length === 4 &&
    endYear.length === 4
  );
});

function getCompanyName(company) {
  if (!company) return "";

  if (typeof company === "string") {
    return company.trim();
  }

  return String(company.name || "").trim();
}

function extractYear(value) {
  return String(value || "")
    .replace(/\D/g, "")
    .slice(0, 4);
}

function formatYear(field) {
  const year = extractYear(form.value[field]);
  form.value[field] = year ? `${year} г.` : "";
}

function resetForm() {
  form.value = {
    company: null,
    specialty: "",
    start_date: "",
    end_date: ""
  };
}

function close() {
  if (isSaving.value) return;

  resetForm();
  emit("close");
}

async function handleSave() {
  if (!isFormValid.value || isSaving.value) return;

  const payload = {
    company: getCompanyName(form.value.company),
    specialty: form.value.specialty.trim(),
    start_date: extractYear(form.value.start_date),
    end_date: extractYear(form.value.end_date)
  };

  isSaving.value = true;

  try {
    const response = await SummaryService.addCareer(payload);

    /*
     Поддерживаем несколько возможных форматов ответа API:

     1. { id, company, ... }
     2. { career: { id, company, ... } }
     3. { data: { id, company, ... } }
    */
    const responseCareer =
      response?.career ||
      response?.data ||
      response ||
      {};

    const savedCareer = {
      ...payload,
      ...responseCareer
    };

    console.log("✅ Карьера успешно добавлена:", savedCareer);

    emit("save", savedCareer);

    emit("snackbar", {
      message: "Опыт работы успешно добавлен",
      type: "success"
    });

    resetForm();
    emit("close");

  } catch (error) {
    console.error("❌ Ошибка при сохранении карьеры:", error);

    emit("snackbar", {
      message: "Не удалось добавить опыт работы",
      type: "error"
    });

  } finally {
    isSaving.value = false;
  }
}
</script>

<style scoped>
@import "./drawer-styles.css";

/* =========================================================
   DRAWER HEADER
========================================================= */

.drawer-header {
  min-height: 82px;
  padding: 18px 28px;

  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;

  border-bottom: 1px solid #eceef1;
}

.drawer-title {
  margin: 0;

  color: #1c1c1e;
  font-family: "Eastman-Bold", sans-serif;
  font-size: 20px;
  line-height: 1.25;
}

.drawer-subtitle {
  margin: 5px 0 0;

  color: #898b91;
  font-family: "Eastman-Regular", sans-serif;
  font-size: 13px;
  line-height: 1.4;
}

.close-button {
  width: 40px;
  height: 40px;
  flex-shrink: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  padding: 0;
  border: none;
  border-radius: 50%;

  background: transparent;
  color: #252527;

  font-size: 19px;
  cursor: pointer;

  transition:
    background-color 0.2s ease,
    color 0.2s ease,
    transform 0.15s ease;
}

.close-button:hover:not(:disabled) {
  background: #f1f2f4;
  transform: rotate(4deg);
}

.close-button:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}


/* =========================================================
   DRAWER BODY
========================================================= */

.drawer-body {
  position: relative;
  flex: 1;
  min-height: 0;

  padding: 28px;
  overflow-y: auto;
}

.form-content {
  transition:
    opacity 0.2s ease,
    filter 0.2s ease;
}

.form-content--disabled {
  opacity: 0.5;
  filter: grayscale(0.15);
  pointer-events: none;
}


/* =========================================================
   FORM
========================================================= */

.career-fields {
  margin-top: 26px;

  display: flex;
  flex-direction: column;
  gap: 24px;
}

.field-group,
.date-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.field-label {
  color: #55575c;

  font-family: "Eastman-Medium", sans-serif;
  font-size: 13px;
  line-height: 1.3;
}

.text-input {
  width: 100%;
  height: 52px;
  box-sizing: border-box;

  padding: 0 16px;

  border: 1px solid transparent;
  border-radius: 16px;
  outline: none;

  background: #f4f5f7;
  color: #252527;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 15px;

  transition:
    background-color 0.2s ease,
    border-color 0.2s ease,
    box-shadow 0.2s ease;
}

.text-input:hover:not(:disabled) {
  background: #eff0f2;
}

.text-input:focus {
  background: #ffffff;
  border-color: rgba(40, 135, 247, 0.55);
  box-shadow: 0 0 0 4px rgba(40, 135, 247, 0.1);
}

.text-input::placeholder {
  color: #999ca2;
}

.text-input:disabled {
  cursor: not-allowed;
}

.input-full {
  max-width: 100%;
}

.date-fields {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}


/* =========================================================
   SAVING OVERLAY
========================================================= */

.saving-overlay {
  position: absolute;
  inset: 0;
  z-index: 10;

  display: flex;
  align-items: center;
  justify-content: center;

  padding: 24px;

  background: rgba(255, 255, 255, 0.35);
  backdrop-filter: blur(2px);
}

.saving-card {
  display: flex;
  align-items: center;
  gap: 14px;

  padding: 16px 20px;

  border: 1px solid #e8ebef;
  border-radius: 18px;

  background: #ffffff;
  box-shadow: 0 12px 34px rgba(23, 40, 65, 0.14);
}

.saving-spinner {
  width: 28px;
  height: 28px;
  flex-shrink: 0;

  border: 3px solid rgba(40, 135, 247, 0.18);
  border-top-color: #2887f7;
  border-radius: 50%;

  animation: spin 0.65s linear infinite;
}

.saving-text {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.saving-text strong {
  color: #242426;
  font-family: "Eastman-Medium", sans-serif;
  font-size: 14px;
}

.saving-text span {
  color: #8a8d93;
  font-family: "Eastman-Regular", sans-serif;
  font-size: 12px;
}


/* =========================================================
   FOOTER
========================================================= */

.drawer-footer {
  min-height: 76px;
  padding: 12px 28px;

  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;

  border-top: 1px solid #eceef1;
  background: #ffffff;
}

.cancel-button,
.save-btn {
  height: 46px;

  padding: 0 24px;

  border: none;
  border-radius: 15px;

  font-family: "Eastman-Medium", sans-serif;
  font-size: 14px;

  cursor: pointer;

  transition:
    background-color 0.2s ease,
    color 0.2s ease,
    opacity 0.2s ease,
    box-shadow 0.2s ease,
    transform 0.15s ease;
}

.cancel-button {
  background: #f0f1f3;
  color: #45474c;
}

.cancel-button:hover:not(:disabled) {
  background: #e6e7e9;
}

.save-btn {
  min-width: 145px;

  display: flex;
  align-items: center;
  justify-content: center;
  gap: 9px;

  background: #2887f7;
  color: #ffffff;

  box-shadow: 0 7px 18px rgba(40, 135, 247, 0.2);
}

.save-btn:hover:not(:disabled) {
  background: #1878e9;
  box-shadow: 0 9px 22px rgba(40, 135, 247, 0.28);
  transform: translateY(-1px);
}

.save-btn:disabled {
  background: #d7d9dd;
  color: #96999f;

  box-shadow: none;
  cursor: not-allowed;
}

.cancel-button:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.button-spinner {
  width: 17px;
  height: 17px;

  border: 2px solid rgba(255, 255, 255, 0.4);
  border-top-color: #ffffff;
  border-radius: 50%;

  animation: spin 0.65s linear infinite;
}


/* =========================================================
   ANIMATIONS
========================================================= */

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.slide-left-enter-active,
.slide-left-leave-active {
  transition:
    transform 0.28s cubic-bezier(0.22, 1, 0.36, 1),
    opacity 0.22s ease;
}

.slide-left-enter-from,
.slide-left-leave-to {
  transform: translateX(-100%);
  opacity: 0;
}

.slide-left-enter-to,
.slide-left-leave-from {
  transform: translateX(0);
  opacity: 1;
}

.fields-fade-enter-active,
.fields-fade-leave-active {
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
}

.fields-fade-enter-from,
.fields-fade-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}


/* =========================================================
   DARK THEME
========================================================= */

html[data-theme="dark"] .drawer-header,
html[data-theme="dark"] .drawer-footer {
  border-color: #3a3a3d;
}

html[data-theme="dark"] .drawer-title {
  color: #f1f1f2;
}

html[data-theme="dark"] .drawer-subtitle {
  color: #9a9a9f;
}

html[data-theme="dark"] .close-button {
  color: #eeeeef;
}

html[data-theme="dark"] .close-button:hover:not(:disabled) {
  background: #333336;
}

html[data-theme="dark"] .field-label {
  color: #c8c8cc;
}

html[data-theme="dark"] .text-input {
  background: #2c2c2f;
  border-color: transparent;
  color: #eeeeef;
}

html[data-theme="dark"] .text-input:hover:not(:disabled) {
  background: #323235;
}

html[data-theme="dark"] .text-input:focus {
  background: #29292c;
  border-color: rgba(75, 151, 255, 0.65);
  box-shadow: 0 0 0 4px rgba(75, 151, 255, 0.12);
}

html[data-theme="dark"] .text-input::placeholder {
  color: #85858b;
}

html[data-theme="dark"] .saving-overlay {
  background: rgba(25, 25, 27, 0.45);
}

html[data-theme="dark"] .saving-card {
  background: #2c2c2f;
  border-color: #414145;
  box-shadow: 0 12px 34px rgba(0, 0, 0, 0.4);
}

html[data-theme="dark"] .saving-text strong {
  color: #eeeeef;
}

html[data-theme="dark"] .saving-text span {
  color: #9d9da3;
}

html[data-theme="dark"] .drawer-footer {
  background: #222224;
}

html[data-theme="dark"] .cancel-button {
  background: #353538;
  color: #dddddf;
}

html[data-theme="dark"] .cancel-button:hover:not(:disabled) {
  background: #404044;
}

html[data-theme="dark"] .save-btn:disabled {
  background: #3d3d40;
  color: #77777c;
}


/* =========================================================
   RESPONSIVE
========================================================= */

@media (max-width: 640px) {
  .drawer-header {
    padding: 16px 18px;
  }

  .drawer-title {
    font-size: 18px;
  }

  .drawer-body {
    padding: 22px 18px;
  }

  .date-fields {
    grid-template-columns: 1fr;
  }

  .drawer-footer {
    padding: 12px 18px;
  }

  .cancel-button {
    display: none;
  }

  .save-btn {
    width: 100%;
  }
}
</style>